import React, { ForwardedRef, useCallback, useMemo } from "react";
import { GestureResponderEvent, StyleSheet, View } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import {
    DateScheduleType,
    DayScheduleType,
    ScheduleTimeType,
    ScheduleType,
    TimeMode,
    WorkingTimeSpecificType,
} from "../../__generated__/graphql";
import Button from "../Button";
import TimeListComponent, { verifyScheduleTypesFixedTimes } from "./TimeListComponent";
import { useI18n } from "../../hooks/i18n-hooks";
import { Buildable } from "ts-essentials";
import VStack from "../../ui_atoms/VStack";
import { useSnackbar } from "../../hooks/snackbar";
import IconButton from "../IconButton";
import Text from "../Text";
import { DatePicker } from "../DatePicker";
import { format } from "date-fns";
import { useActionSheetOrDropdown } from "../../hooks/use-action-sheet-or-dropdown";
import { DropdownItemsType } from "../../contexts/DropdownManager";
import TimeField from "../TimeField";
import { FULL_DATE_FORMAT, useDateFormatter } from "../../hooks/date";

const formatDate = (date: Date): string => {
    return format(date, "yyyy-MM-dd");
};

const createNewDateSchedule = (date: Date, timesTemplate?: DayScheduleType): DateScheduleType => {
    if (!timesTemplate) {
        timesTemplate = {
            timeMode: TimeMode.Free,
            freeDurations: [{ duration: 504, type: ScheduleTimeType.Work }],
        };
    }
    return {
        date: formatDate(date),
        daySchedule: {
            ...timesTemplate,
        },
    };
};

const getLastDateSchedule = (times: DateScheduleType[]): DateScheduleType | null => {
    if (times && times.length > 0) {
        return times[times.length - 1];
    }
    return null;
};

type DateScheduleCellProps = {
    dateSchedule: DateScheduleType;
    onChange: (dateSchedule: DateScheduleType) => void;
    onShowMenu: (e?: GestureResponderEvent) => void;
    disabled?: boolean;
    menuRef?: ForwardedRef<View>;
};

const TimeAsMinuteInput: React.FC<{
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
    return (
        <TimeField
            value={value}
            onChange={onChange}
            isDuration
            minuteInterval={1}
            disabled={disabled}
            style={{
                flexGrow: 1,
                flexShrink: 1,
                maxWidth: null,
                height: 40,
                marginTop: 20,
                marginBottom: 20,
            }}
            inputStyle={{
                textAlign: "center",
            }}
        />
    );
};

const DateScheduleCell: React.FC<DateScheduleCellProps> = ({
    dateSchedule,
    onChange,
    onShowMenu,
    disabled = false,
    menuRef,
}) => {
    const componentStyle = useDateScheduleCellStyle();
    const i18n = useI18n();
    const onChangeTimes = useCallback(
        (times: ScheduleType[]) => {
            onChange({
                ...dateSchedule,
                daySchedule: {
                    timeMode: TimeMode.Fixed,
                    fixedTimes: [...times],
                },
            });
        },
        [onChange, dateSchedule],
    );
    const onChangeFreeDuration = useCallback(
        (freeDurations: number) => {
            onChange({
                ...dateSchedule,
                daySchedule: {
                    timeMode: TimeMode.Free,
                    freeDurations: [
                        {
                            duration: freeDurations,
                            type: ScheduleTimeType.Work,
                        },
                    ],
                },
            });
        },
        [onChange, dateSchedule],
    );
    const intlFormat = useDateFormatter();
    const errors: string[] = useMemo(() => {
        return verifyDateSchedule([dateSchedule]);
    }, [dateSchedule]);

    const day = useMemo<string>(() => {
        return format(new Date(dateSchedule.date), "yyyy-MM-dd");
    }, [dateSchedule.date]);

    return (
        <View style={componentStyle.container}>
            <View style={componentStyle.header}>
                <Text style={componentStyle.headerText} ellipsizeMode="tail" numberOfLines={1}>
                    {intlFormat(new Date(dateSchedule.date), FULL_DATE_FORMAT)}
                </Text>
                <View style={componentStyle.headerButton}>
                    <IconButton
                        ref={menuRef}
                        size={24}
                        icon="trash"
                        onPress={onShowMenu}
                        disabled={disabled}
                    />
                </View>
            </View>

            {errors.map((err) => (
                <Text key={err} style={componentStyle.errorText}>
                    {i18n.t(err)}
                </Text>
            ))}

            {dateSchedule.daySchedule?.timeMode === TimeMode.Free &&
                dateSchedule.daySchedule.freeDurations && (
                    <View style={componentStyle.modeFree}>
                        <TimeAsMinuteInput
                            value={dateSchedule.daySchedule.freeDurations[0].duration || 0}
                            onChange={onChangeFreeDuration}
                            disabled={disabled}
                        />
                    </View>
                )}
            {dateSchedule.daySchedule?.timeMode === TimeMode.Fixed && (
                <TimeListComponent
                    times={dateSchedule.daySchedule.fixedTimes || []}
                    onChange={onChangeTimes}
                    disabled={disabled}
                    day={day}
                />
            )}
        </View>
    );
};

const useDateScheduleCellStyle = () => {
    const { gridSize, colors, marginSize, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingTop: gridSize / 2,
                    paddingBottom: gridSize / 2,
                },
                icon: {
                    paddingRight: gridSize / 2,
                    height: gridSize,
                    width: gridSize,
                    paddingTop: 2,
                    marginLeft: -2,
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "row",
                },
                header: {
                    flexDirection: "row",
                    alignItems: "center",
                    paddingLeft: marginSize,
                },
                headerText: {
                    flex: 1,
                    textTransform: "capitalize",
                    flexGrow: 1,
                    ...fonts.bodyMedium,
                },
                headerButton: {
                    flexGrow: 0,
                    textAlign: "right",
                    marginRight: marginSize,
                },
                errorText: {
                    color: colors.error,
                    alignSelf: "center",
                    marginBottom: gridSize / 2,
                },
                modeFree: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: marginSize,
                    width: "100%",
                },
            }),
        [gridSize, marginSize, colors.error, fonts],
    );
};

type WorkingTimeSpecificDatesComponentProps = {
    workingTime: WorkingTimeSpecificType;
    onChange: (workingTime: WorkingTimeSpecificType) => void;
    disabled?: boolean;
};

const WorkingTimeSpecificDatesComponent: React.FC<WorkingTimeSpecificDatesComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
}) => {
    const componentStyle = useWorkingTimeSpecificStyle();
    const [showDatePicker, setShowDatePicker] = React.useState(false);

    const { showSnackbar } = useSnackbar();

    const i18n = useI18n();
    const onAddDate = useCallback(
        (date: Date) => {
            setShowDatePicker(false);
            const timesTemplate = getLastDateSchedule(workingTime.dates)?.daySchedule;
            const newDate = createNewDateSchedule(date, timesTemplate || undefined);
            if (workingTime.dates.some((d) => newDate.date === d.date)) {
                showSnackbar({ mode: "error", text: i18n.t("workingTime.date_already_added") });
                return;
            }

            onChange({ dates: [...workingTime.dates, newDate] });
        },
        [workingTime.dates, onChange, showSnackbar, i18n],
    );

    const onStartAddingDate = useCallback(() => {
        setShowDatePicker(true);
    }, []);

    const onRemoveDateAtIndex = useCallback(
        (idx: number) => {
            const newDates = [...workingTime.dates];
            newDates.splice(idx, 1);
            onChange({ dates: [...newDates] });
        },
        [onChange, workingTime],
    );
    const onChangeDateSchedule = useCallback(
        (idx: number, dateSchedule: DateScheduleType) => {
            const newDates = [...workingTime.dates];
            newDates[idx] = dateSchedule;
            onChange({ dates: [...newDates] });
        },
        [onChange, workingTime],
    );

    const dropdownItems = useMemo<DropdownItemsType[]>(
        () => [
            {
                label: i18n.t("workingTime.removeDate"),
                onClick: ({ idx }: { idx: number }) => {
                    onRemoveDateAtIndex(idx);
                },
            },
        ],
        [i18n, onRemoveDateAtIndex],
    );

    const dropdownProps = useActionSheetOrDropdown({
        items: dropdownItems,
        destructiveButtonIndex: 0,
        // position: "right",
        // setIsLoading,
    });

    const nextDate = useMemo(() => {
        const last =
            workingTime.dates.length > 0
                ? new Date(workingTime.dates[workingTime.dates.length - 1].date)
                : new Date();
        const next = new Date(last);
        next.setDate(next.getDate() + 1);
        return next;
    }, [workingTime]);

    return (
        <>
            <VStack gap={0} alignment="stretch">
                {workingTime.dates.map((dateSchedule, idx) => (
                    <View key={dateSchedule.date}>
                        <DateScheduleCell
                            dateSchedule={dateSchedule}
                            menuRef={dropdownProps.ref}
                            onChange={(dateSchedule) => {
                                onChangeDateSchedule(idx, dateSchedule);
                            }}
                            onShowMenu={(e) => {
                                dropdownProps.onPress(e, { dateSchedule, idx });
                            }}
                            disabled={disabled}
                        />
                        <View style={componentStyle.separator} />
                    </View>
                ))}

                <View style={componentStyle.actions}>
                    <DatePicker
                        date={nextDate}
                        display="inline"
                        isVisible={showDatePicker}
                        mode="date"
                        onConfirm={onAddDate}
                        onCancel={() => setShowDatePicker(false)}
                        minimumDate={new Date()}
                    />
                    <Button
                        label={i18n.t("workingTime.addDate")}
                        onPress={onStartAddingDate}
                        disabled={disabled}
                        buttonMode="outlined"
                    />
                </View>
            </VStack>
        </>
    );
};

const useWorkingTimeSpecificStyle = () => {
    const { shadow, roundness, colors, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    borderWidth: 1,
                    borderRadius: roundness,
                    borderColor: colors.backdrop,
                    backgroundColor: colors.card,
                    ...shadow,
                },
                actions: {
                    padding: gridSize,
                },
                separator: {
                    height: 4,
                    flex: 1,
                    alignSelf: "stretch",
                    backgroundColor: colors.accentLighter,
                },
            }),
        [shadow, roundness, colors, gridSize],
    );
};
export default WorkingTimeSpecificDatesComponent;

export const verifyWorkingTimeSpecificType = (
    value?: Buildable<WorkingTimeSpecificType> | null,
): string[] => {
    if (!value) {
        return [];
    }
    return verifyDateSchedule(value.dates);
};

export const verifyDateSchedule = (
    dateSchedules?: Array<Buildable<DateScheduleType | undefined>> | null,
): string[] => {
    return Array.from(
        (dateSchedules || []).reduce((acc, dateSchedule) => {
            const errors =
                dateSchedule && dateSchedule.daySchedule && dateSchedule.daySchedule.fixedTimes
                    ? verifyScheduleTypesFixedTimes(dateSchedule.daySchedule.fixedTimes)
                    : [];
            return new Set([...acc, ...errors]);
        }, new Set<string>()),
    );
};
